import React, { useEffect, useState } from "react";
import { StatusPill, challengeChannelHint, formatRelative } from "../shared";
import type { ChallengeInfo } from "../../params/types";

interface ChallengeBannerProps {
  challenge?: ChallengeInfo | null;
  busy?: boolean;
  onSubmit: (code: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
  onEmptyCode?: () => void;
}

export default function ChallengeBanner({
  challenge,
  busy,
  onSubmit,
  onCancel,
  onEmptyCode,
}: ChallengeBannerProps) {
  const [code, setCode] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const challengeOpen = Boolean(challenge?.pending);
  const challengeSubmitted = Boolean(challenge?.code_submitted);
  const challengeExpiresAt = challenge?.expires_at
    ? new Date(challenge.expires_at).getTime()
    : 0;

  useEffect(() => {
    if (!challengeOpen) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [challengeOpen, challenge?.id]);

  const challengeVisible =
    challengeOpen && (!challengeExpiresAt || now < challengeExpiresAt);
  const challengeExpiresInSec = challengeExpiresAt
    ? Math.max(0, Math.ceil((challengeExpiresAt - now) / 1000))
    : null;

  if (!challengeVisible) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      onEmptyCode?.();
      return;
    }
    const ok = await onSubmit(trimmed);
    if (ok) setCode("");
  };

  const handleCancel = async () => {
    const ok = await onCancel();
    if (ok) setCode("");
  };

  return (
    <section className="ib-card ib-challenge-card">
      <div className="ib-challenge-head">
        <StatusPill
          status={challengeSubmitted ? "CODE_SENT" : "NEED_CODE"}
          running
        />
        <div>
          <div className="ib-challenge-title">
            {challengeSubmitted
              ? "Code submitted — waiting for Instagram…"
              : "Verification code required"}
          </div>
          <div className="ib-muted">
            Account <strong>{challenge?.account || "—"}</strong>
            {challenge?.channel || challenge?.type
              ? ` · ${challenge?.channel || challenge?.type}`
              : ""}
            {challenge?.destination ? ` · ${challenge.destination}` : ""}
            {challenge?.requested_at
              ? ` · requested ${formatRelative(challenge.requested_at)}`
              : ""}
            {challengeExpiresInSec != null
              ? ` · expires in ${Math.floor(
                  challengeExpiresInSec / 60,
                )}:${String(challengeExpiresInSec % 60).padStart(2, "0")}`
              : ""}
          </div>
        </div>
      </div>
      <p className="ib-challenge-msg">{challenge?.message}</p>
      {challenge?.destination && (
        <p className="ib-challenge-msg">
          Look for the code on{" "}
          <strong>
            {challenge.channel || challenge.type || "destination"}
          </strong>
          : <strong>{challenge.destination}</strong>
        </p>
      )}
      {!challenge?.destination &&
        challengeChannelHint(challenge?.channel || challenge?.type) && (
          <p className="ib-challenge-msg ib-muted">
            Check your{" "}
            <strong>
              {challengeChannelHint(challenge?.channel || challenge?.type)}
            </strong>
            .
          </p>
        )}
      {!challengeSubmitted ? (
        <form className="ib-challenge-form" onSubmit={handleSubmit}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- challenge banner; focusing the code field is expected
            autoFocus
            maxLength={12}
          />
          <button
            className="jf-btn jf-btn-primary-light jf-btn-sm"
            type="submit"
            disabled={busy || !code.trim()}
          >
            Submit code
          </button>
          <button
            className="jf-btn jf-btn-sm"
            type="button"
            disabled={busy}
            onClick={handleCancel}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="ib-challenge-form">
          <p className="ib-muted" style={{ margin: 0 }}>
            Waiting for the bot to apply the code on Instagram…
          </p>
          <button
            className="jf-btn jf-btn-sm"
            type="button"
            disabled={busy}
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
